import NotFound from "./NotFound";
import "./calculators.css";
import CivilTopicNavigation from "@/components/marketing/CivilTopicNavigation";
import civilTopics from "@/data/civilTopics.json";
import { GUIDES } from "@/data/civilGuides";
import { CALCULATORS, evaluateCalculator, calculatorSchema, CIVIL_HUB_META } from "@/data/civilCalculators";
// Fieldlink Civil Hub (/resources) + free working calculators (/tools/*).
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Search, Copy, Check, FileText, Library } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PublicFooter } from "../FAQ";
import { MarketingHeader, Breadcrumb, breadcrumbJsonLd } from "./SeoProductPage";


// lightweight, anonymous Civil Hub analytics (never sends calculator inputs)
export const hubTrack = (kind, data = {}) => {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/civilhub/event`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind, ...data }),
    }).catch(() => {});
  } catch {}
};

export { CALCULATORS };

// Lightweight index of Civil Hub templates (routes live in Templates.jsx).
export const TEMPLATE_LINKS = [
  { slug: "itp-template", name: "ITP Template", cat: "QA & Safety", desc: "Inspection & Test Plan — activities, criteria, hold points, sign-off." },
  { slug: "construction-variation-template", name: "Variation Template", cat: "Commercial", desc: "Price a variation with markup and GST, print-ready." },
  { slug: "progress-claim-template", name: "Progress Claim Template", cat: "Commercial", desc: "Claim value from work to date, previous claims and retention." },
  { slug: "pre-start-template", name: "Pre-Start Template", cat: "QA & Safety", desc: "Daily pre-start — crew, plant, hazards, controls, sign-off." },
  { slug: "site-diary-template", name: "Site Diary Template", cat: "QA & Safety", desc: "Daily record — weather, crew, work done, deliveries, delays." },
  { slug: "toolbox-talk-template", name: "Toolbox Talk Template", cat: "QA & Safety", desc: "Topic, discussion points, hazards and attendee sign-on." },
  { slug: "plant-inspection-template", name: "Plant Inspection Template", cat: "QA & Safety", desc: "Pre-operation plant checks, defects and safe/unsafe status." },
];

// Lightweight index of Civil Hub guides (routes live in Guides.jsx).
export const GUIDE_LINKS = GUIDES.map(g => ({ slug: g.slug, name: g.name, desc: g.description }));

export function CalculatorPage({ slug }) {
  const calculator = CALCULATORS.find(c => c.slug === slug);
  return calculator ? <CalculatorContent key={slug} c={calculator} /> : <NotFound />;
}

function CalculatorContent({ c }) {
  const [vals, setVals] = useState(() => Object.fromEntries(c.fields.map(f => [f.key, f.def])));
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const copyTimer = useRef(null);
  const calculation = useMemo(() => evaluateCalculator(c, vals), [c, vals]);
  const { rows, error, errors, breakdown, inputs } = calculation;
  useEffect(() => { hubTrack("calculator_started", { tool: c.slug }); }, [c.slug]);
  useEffect(() => () => clearTimeout(copyTimer.current), []);
  const change = (key, value) => {
    clearTimeout(copyTimer.current);
    setCopied(false); setCopyError(false);
    setVals(previous => ({ ...previous, [key]: value }));
  };
  const resultText = [
    `${c.name} — Fieldlink Civil Hub`, ...inputs, "", ...rows.map(row => `${row.label}: ${row.value}`),
    ...(breakdown.length ? ["", "Calculation breakdown (display values rounded):", ...breakdown] : []),
    ...(c.notes ? ["", `Assumptions: ${c.notes}`] : []),
    ...(c.disclaimer ? [c.disclaimer] : []),
    `https://www.fieldlink.co.nz/tools/${c.slug}`,
  ].join("\n");
  const copyResult = async () => {
    if (error || !rows.length) return;
    try {
      await navigator.clipboard.writeText(resultText);
      setCopied(true); setCopyError(false);
      hubTrack("calculator_result", { tool: c.slug });
      copyTimer.current = setTimeout(() => setCopied(false), 2500);
    } catch { setCopyError(true); }
  };
  const trail = [{ label: "Home", to: "/" }, { label: "Civil Hub", to: "/resources" }, { label: c.name }];
  return <div className="web-seo civil-calculator bg-[#f6f5f1] text-[#142635] min-h-screen">
    <SEO title={c.title} description={c.description} path={`/tools/${c.slug}`} jsonLd={[calculatorSchema(c), breadcrumbJsonLd(trail)]} />
    <a className="web-skip" href="#calculator">Skip to calculator</a>
    <MarketingHeader /><Breadcrumb trail={trail} />
    <main id="calculator" className="max-w-6xl mx-auto px-6 md:px-12 py-10">
      <p className="text-sm font-bold tracking-widest text-[#a63c0b] mb-3">Free civil tool · Metric · No signup</p>
      <h1 className="font-display font-bold text-3xl sm:text-4xl mb-4">{c.name}</h1>
      <p className="text-[#344451] max-w-3xl mb-4">{c.intro}</p>
      <p className="text-sm text-[#465562] mb-7">Example inputs are editable. Use your project values. Enter 0 where no optional allowance is intended; blank fields pause the calculation.</p>
      <div className="calc-layout">
        <section className="calc-panel" aria-labelledby="calc-input-heading" data-testid={`calc-inputs-${c.slug}`}>
          <h2 id="calc-input-heading" className="text-xl font-bold mb-5">Your inputs</h2>
          {c.fields.filter(f => !f.showIf || f.showIf(vals)).map(f => <div key={f.key} className="calc-field">
            <label htmlFor={`input-${f.key}`}>{f.label}{f.unit && <span className="text-[#465562]"> ({f.unit})</span>}</label>
            {f.type === "select" ? <select id={`input-${f.key}`} value={vals[f.key]} onChange={e => change(f.key, e.target.value)} aria-invalid={!!errors[f.key]} aria-describedby={errors[f.key] ? `error-${f.key}` : undefined} data-testid={`calc-input-${f.key}`}>
              {f.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select> : <input id={`input-${f.key}`} type="number" inputMode="decimal" min={Number.isFinite(f.min) ? f.min : undefined} max={f.max} step={f.step || "any"} value={vals[f.key]} onChange={e => change(f.key, e.target.value)} aria-invalid={!!errors[f.key]} aria-describedby={errors[f.key] ? `error-${f.key}` : undefined} data-testid={`calc-input-${f.key}`} />}
            {errors[f.key] && <p className="calc-error" id={`error-${f.key}`}>{errors[f.key]}</p>}
          </div>)}
        </section>
        <section className="calc-panel" aria-labelledby="calc-result-heading" data-testid={`calc-results-${c.slug}`}>
          <h2 id="calc-result-heading" className="text-xl font-bold mb-5">Your results</h2>
          <div aria-live="polite" aria-atomic="true">
            {error ? <p className="calc-error" role="status">{error}</p> : <dl className="calc-results">{rows.map(row => <div key={row.label}><dt>{row.label}</dt><dd className={row.strong ? "calc-strong" : ""}>{row.value}</dd></div>)}</dl>}
          </div>
          {!error && breakdown.length > 0 && <div className="calc-breakdown"><h3 className="text-lg font-bold mb-3">Calculation breakdown</h3>{breakdown.map(line => <p key={line}>{line}</p>)}<p className="text-sm text-[#465562] mt-3">Displayed values are rounded. The calculation uses unrounded inputs.</p></div>}
          <button onClick={copyResult} disabled={!!error || !rows.length} className="calc-copy" data-testid="calc-copy-result">{copied ? <Check size={18} /> : <Copy size={18} />}{copied ? "Copied" : "Copy inputs and results"}</button>
          {copyError && !error && <div className="mt-4"><p role="status" className="text-sm mb-2">Clipboard access was unavailable. Select and copy this text:</p><textarea aria-label="Calculation text to copy" readOnly value={resultText} rows={9} className="calc-copy-text" onFocus={e => e.target.select()} /></div>}
          <p className="text-sm text-[#465562] mt-4">These free results do not automatically save or sync to a Fieldlink job.</p>
        </section>
      </div>
      <section className="calc-explanation" aria-labelledby="calc-formula-heading">
        <h2 id="calc-formula-heading" className="text-2xl font-bold mb-4">How it works</h2>
        <ul className="space-y-2">{c.formulas.map(formula => <li key={formula}>{formula}</li>)}</ul>
        {c.example && <div className="calc-example"><h3 className="text-xl font-bold mb-3">Worked example</h3><p className="whitespace-pre-line">{c.example}</p></div>}
        {(c.notes || c.disclaimer) && <div className="calc-example"><h3 className="text-xl font-bold mb-3">Assumptions and practical notes</h3>{c.notes && <p>{c.notes}</p>}{c.disclaimer && <p className="mt-3">{c.disclaimer}</p>}</div>}
      </section>
      <section className="calc-product"><div><h2 className="text-2xl font-bold mb-3">Working on the full job?</h2><p>{c.product.text}</p></div><Link to={c.product.to} className="calc-copy">{c.product.label}<ArrowRight size={18} /></Link></section>
      <nav aria-label="Related tools and guides" className="calc-related"><h2 className="text-xl font-bold mb-3">Related tools and guides</h2><ul>{c.related.map(url => {
        const calculator = CALCULATORS.find(item => `/tools/${item.slug}` === url);
        const guide = GUIDE_LINKS.find(item => `/guides/${item.slug}` === url);
        return <li key={url}><Link to={url}>{calculator?.name || guide?.name || url.split('/').pop().replace(/-/g, ' ')}</Link></li>;
      })}<li><Link to="/resources">All Civil Hub tools</Link></li></ul></nav>
    </main><PublicFooter />
  </div>;
}

const POPULAR = ["excavation-volume-calculator", "pipe-grade-calculator", "trench-volume-calculator", "labour-charge-out-rate-calculator-nz", "excavator-charge-out-rate-calculator-nz", "markup-margin-calculator"];
const CAT_ORDER = ["Earthworks", "Drainage", "Materials", "Plant", "Business"];
const CAT_LABEL = { Plant: "Plant & Cartage", Business: "Commercial" };

export function CivilHub() {
  const trail = [{ label: "Home", to: "/" }, { label: "Civil Hub" }];
  const [q, setQ] = useState("");
  const timer = useRef(null);
  const index = useMemo(() => [
    ...civilTopics.flatMap(topic => topic.links.filter(link => link.kind !== "Guide").map(link => ({ to: link.to, name: link.label, cat: topic.name, desc: topic.description, kind: link.kind }))),
    ...CALCULATORS.map((c) => ({ to: `/tools/${c.slug}`, name: c.name, cat: CAT_LABEL[c.cat] || c.cat, desc: c.intro, outputs: c.outputSummary, kind: "Calculator" })),
    ...TEMPLATE_LINKS.map((t) => ({ to: `/templates/${t.slug}`, name: t.name, cat: t.cat, desc: t.desc, kind: "Template" })),
    ...GUIDE_LINKS.map((g) => ({ to: `/guides/${g.slug}`, name: g.name, cat: "Guides", desc: g.desc, kind: "Guide" })),
    { to: "/civil-rate-library-nz", name: "Civil Rate Library NZ", cat: "Rate Library", desc: "Build labour, plant, material and cartage rates from your own costs.", kind: "Guide" },
  ], []);
  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return null;
    const tokens = s.split(/\s+/).filter(Boolean);
    return index.filter((x) => {
      const hay = `${x.name} ${x.cat} ${x.desc} ${x.kind}`.toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [q, index]);
  useEffect(() => {
    if (q.trim().length < 3) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => hubTrack(hits && hits.length ? "civil_hub_search" : "civil_hub_search_no_results", { term: q.trim().toLowerCase(), results: hits ? hits.length : 0 }), 900);
    return () => clearTimeout(timer.current);
  }, [q, hits]);

  const ToolCard = ({ x }) => (
    <Link to={x.to} className="group rounded-lg border border-[#cbd3d5] bg-white hover:border-[#FF5F15]/60 p-4 transition-colors" data-testid={`hub-item-${x.to.split("/").pop()}`}>
      <div className="flex items-center justify-between">
        {x.kind === "Template" ? <FileText className="w-4 h-4 text-[#a63c0b]" /> : x.kind === "Guide" ? <Library className="w-4 h-4 text-[#a63c0b]" /> : <Calculator className="w-4 h-4 text-[#a63c0b]" />}
        <ArrowRight className="w-4 h-4 text-[#52616c] group-hover:text-[#a63c0b]" />
      </div>
      <p className="font-display font-bold uppercase tracking-tight mt-2">{x.name}</p>
      <p className="text-xs text-[#52616c] mt-1">{x.desc}</p>
      <p className="text-sm text-[#465562] mt-3">{x.outputs || x.kind}</p><span className="inline-flex items-center gap-2 text-sm text-[#a63c0b] font-semibold mt-4">{x.kind === "Calculator" ? "Open calculator" : x.kind === "Template" ? "Open template" : x.kind === "Software" ? "Explore software" : "Read guide"}<ArrowRight size={16} /></span>
    </Link>
  );

  const software = [
    { to: "/civil-construction-software-nz", label: "Civil Construction Software NZ" },
    { to: "/civil-estimating-software-nz", label: "Civil Estimating Software" },
    { to: "/construction-takeoff-software-nz", label: "Construction Takeoff Software" },
    { to: "/ai-construction-plan-reader", label: "AI Plan Reader" },
    { to: "/drainage-estimating-software", label: "Drainage Estimating" },
    { to: "/earthworks-estimating-software", label: "Earthworks Estimating" },
    { to: "/civil-job-costing-software-nz", label: "Civil Job Costing" },
    { to: "/civil-crew-scheduling-software-nz", label: "Crew Scheduling" },
    { to: "/construction-qa-software-nz", label: "Construction QA" },
  ];
  return (
    <div className="bg-[#f6f5f1] text-[#142635] min-h-screen">
      <SEO {...CIVIL_HUB_META} jsonLd={breadcrumbJsonLd(trail)} />
      <MarketingHeader />
      <Breadcrumb trail={trail} />
      {/* hero */}
      <section className="bg-[#f6f5f1] text-[#142635] border-b border-[#cbd3d5] py-14 md:py-16 mt-5">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="block text-[11px] font-mono font-bold uppercase tracking-[0.28em] text-[#a63c0b] mb-4">Fieldlink Civil Hub</div>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.98]">Free tools<br /><span className="text-[#a63c0b]">built for civil.</span></h1>
          <p className="text-[#465562] max-w-2xl mt-4">Calculators, templates, rate guides and practical tools for New Zealand civil contractors. Free to use — no signup.</p>
          <div className="mt-7 max-w-xl relative">
            <Search className="w-4 h-4 text-[#52616c] absolute left-4 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={'Try "pipe grade", "excavator rate", "variation"...'}
              className="w-full h-13 rounded-lg border border-[#cbd3d5] bg-white text-[#142635] placeholder:text-[#52616c] pl-11 pr-4 py-3.5 text-sm font-semibold outline-none focus:border-[#FF5F15]"
              aria-label="Search Civil Hub" data-testid="hub-search" />
          </div>
          <div className="flex items-center gap-1.5 mt-4 flex-wrap">
            <span className="text-[10px] font-mono font-bold text-[#52616c] uppercase tracking-widest mr-1">Popular:</span>
            {POPULAR.map((s) => {
              const c = CALCULATORS.find((x) => x.slug === s);
              return <Link key={s} to={`/tools/${s}`} className="h-7 px-3 inline-flex items-center rounded-full text-[11px] font-bold border border-[#cbd3d5] text-[#465562] hover:border-[#FF5F15] hover:text-[#142635] transition-colors" data-testid={`hub-popular-${s}`}>{c.name.replace(" Calculator", "")}</Link>;
            })}
          </div>
        </div>
      </section>


      <section className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {hits ? (
          <div data-testid="hub-search-results">
            <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-5">{hits.length ? `${hits.length} result${hits.length === 1 ? "" : "s"}` : "No matching resources"}</h2>
            {hits.length === 0 && <p className="text-sm text-[#52616c] mb-4" data-testid="hub-no-results">Nothing matched "{q}". Browse the topics and resources below.</p>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{(hits.length ? hits : index).map((x) => <ToolCard key={x.to} x={x} />)}</div>
          </div>
        ) : (
          <>
            <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-6">Free calculators</h2>
            {CAT_ORDER.filter((cat) => CALCULATORS.some((c) => c.cat === cat)).map((cat) => (
              <div key={cat} className="mb-8">
                <p className="text-[11px] font-mono font-bold uppercase tracking-[0.3em] text-[#a63c0b] mb-3">{CAT_LABEL[cat] || cat}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {CALCULATORS.filter((c) => c.cat === cat).map((c) => (
                    <ToolCard key={c.slug} x={{ to: `/tools/${c.slug}`, name: c.name, desc: c.intro, outputs: c.outputSummary, kind: "Calculator" }} />
                  ))}
                </div>
              </div>
            ))}
            <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-5 mt-12">Free templates</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="hub-templates">
              {TEMPLATE_LINKS.map((t) => <ToolCard key={t.slug} x={{ to: `/templates/${t.slug}`, name: t.name, desc: t.desc, kind: "Template" }} />)}
            </div>
            <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-5 mt-12">How-to guides</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="hub-guides">
              {GUIDE_LINKS.map((g) => <ToolCard key={g.slug} x={{ to: `/guides/${g.slug}`, name: g.name, desc: g.desc, kind: "Guide" }} />)}
            </div>
            <div className="mt-12 rounded-lg bg-white border border-[#cbd3d5] p-6 md:p-8 flex flex-wrap items-center justify-between gap-4" data-testid="hub-rate-library">
              <div className="max-w-xl">
                <p className="text-[11px] font-mono font-bold uppercase tracking-[0.3em] text-[#a63c0b] mb-2">Rate Library</p>
                <h3 className="font-display font-black uppercase tracking-tight text-xl">Civil Rate Library NZ</h3>
                <p className="text-sm text-[#52616c] mt-1.5">How labour, plant, material and cartage rates are built — and why the only rate that matters is one built from your own costs.</p>
              </div>
              <Link to="/civil-rate-library-nz" className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] text-xs font-bold uppercase tracking-widest px-5 py-3 inline-flex items-center gap-1.5">Open rate library <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
          </>
        )}

        {/* workflow CTA */}
        <div className="mt-14 rounded-lg border border-[#cbd3d5] bg-[#e9eeec] p-7 md:p-9" data-testid="hub-cta">
          <p className="text-[11px] font-mono font-bold uppercase tracking-[0.3em] text-[#a63c0b]">Calculators are just the start</p>
          <h3 className="font-display font-black uppercase tracking-tight text-2xl mt-2">Review the quantities. Then price the job.</h3>
          <div className="flex items-center gap-2 flex-wrap mt-4">
            {["Plan", "Takeoff", "Rate", "Estimate", "Quote", "Job", "Cost", "Invoice"].map((s, i, a) => (
              <span key={s} className="inline-flex items-center gap-2">
                <span className="rounded-md border border-[#cbd3d5] bg-white text-[#142635] text-[11px] font-bold uppercase tracking-widest px-3.5 py-2">{s}</span>
                {i < a.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-[#a63c0b]" />}
              </span>
            ))}
          </div>
          <Link to="/features" onClick={() => hubTrack("fieldlink_cta_clicked", { tool: "civil_hub" })} className="mt-6 inline-flex rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider text-sm px-6 py-3 items-center gap-2">See Fieldlink in action <ArrowRight className="w-4 h-4" /></Link>
        </div>

        <p className="text-sm text-[#465562] mt-5">Free calculator results do not automatically sync into the app. For the wider workflow, explore <Link to="/job-management-nz" className="text-[#a63c0b] underline">Fieldlink job management</Link>.</p>
        <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-5 mt-12">Fieldlink software</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {software.map((s) => (
            <Link key={s.to} to={s.to} className="group flex items-center justify-between rounded-md border border-[#cbd3d5] bg-white hover:border-[#FF5F15]/60 px-4 py-3 text-sm font-bold uppercase tracking-wide text-[#344451]">
              {s.label}<ArrowRight className="w-4 h-4 text-[#52616c] group-hover:text-[#a63c0b]" />
            </Link>
          ))}
        </div>
        <p className="text-xs text-[#52616c] mt-10">Need a contractor instead of a calculator? <Link to="/contractors" className="text-[#a63c0b] hover:underline font-bold">Find civil contractors</Link> in the Fieldlink directory.</p>
      </section>
      <CivilTopicNavigation topics={civilTopics} />
      <PublicFooter />
    </div>
  );
}
